import React, { useState } from 'react';
import styled from 'styled-components';
import useAsyncEffect from 'use-async-effect';
import { useDispatch } from 'react-redux';
import { Editor } from '@tinymce/tinymce-react';
import { loading } from '../actions/Loading';
import api from '../api/api';
import AnimatedSaveButton from './AnimatedSaveButton';
import AlertEx from './AlertEx';
import TextInput from './form/TextInput';
import Tab from './Tab';

interface IProps {
    match?: { params: { combinedTicker: string; holderId: string } };
}

const Style = styled.div`
.tab-content {
    margin-top: 1rem;
}
.new-campaign-actions{
    display:flex;
    margin: 1rem 0;
    align-items: center;
}
.new-campaign-actions > * {
    margin-right: 1rem;
}
.new-campaign-subject{
    max-width: 600px;
}
.welcome-email-info{
    margin: 0.5rem 0;
    color: #6b7280;
}
`;

const SendWelcomeEmailPage: React.FC<IProps> = (props: IProps) => {
    const dispatch = useDispatch();
    const combinedTicker = decodeURIComponent(props.match!.params.combinedTicker);
    const holderId = decodeURIComponent(props.match!.params.holderId);
    const query = new URLSearchParams(window.location.search);
    const email = query.get('email') || '';
    const name = query.get('name') || '';
    const [editorContent, setEditorContent] = useState<string>('');
    // the full template document as stored — TinyMCE only edits the <body>, so the
    // original <head> (fonts, media queries, footer styles) is re-attached for preview/send
    const [originalHtml, setOriginalHtml] = useState<string>('');
    const [subject, setSubject] = useState<string>('');
    const [isSending, setIsSending] = useState<boolean>(false);
    const [hasSent, setHasSent] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loaded, setLoaded] = useState<boolean>(false);
    const [editorReady, setEditorReady] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('Editor');

    useAsyncEffect(async (isMounted: () => boolean) => {
        dispatch(loading(true));
        const result = await api().getWelcomeEmailTemplate(combinedTicker);
        if (!isMounted()) return;

        if (result.success) {
            setOriginalHtml(result.html);
            const body = result.html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
            setEditorContent(body ? body[1] : result.html);
            setSubject(result.subject);
        } else {
            setErrorMessage(result.errorMessage);
        }
        if (!email) {
            setErrorMessage('No recipient email supplied');
        }
        setLoaded(true);
        dispatch(loading(false));
    }, []);

    // edited body re-wrapped in the original document (head/styles intact)
    const fullHtml = (): string => {
        if (!/<body[^>]*>[\s\S]*<\/body>/i.test(originalHtml)) return editorContent;
        return originalHtml.replace(/(<body[^>]*>)[\s\S]*(<\/body>)/i, (_, open, close) => `${open}${editorContent}${close}`);
    };

    // the template's <style> blocks, injected into TinyMCE so the editor renders like the real email
    const templateCss = (originalHtml.match(/<style[^>]*>[\s\S]*?<\/style>/gi) || [])
        .map(s => s.replace(/<\/?style[^>]*>/gi, ''))
        .join('\n');

    const sendWelcomeEmail = async () => {
        if (window.confirm(`Send welcome email to ${name} <${email}>?\r\n\r\nThis will also add them as a subscriber.`)) {
            setIsSending(true);
            const result = await api().sendWelcomeEmail(combinedTicker, email, name, subject, fullHtml());
            setIsSending(false);
            if (result.success) {
                setHasSent(true);
                AlertEx('Sent!');
                return true;
            }
            AlertEx(result.errorMessage);
        }
        return false;
    };

    return <Style>
        {!!errorMessage && <p className='error'>{errorMessage}</p>}

        {loaded && !errorMessage && <>
            <div className='new-campaign-actions'>
                <AnimatedSaveButton onClick={sendWelcomeEmail} startingText="Send Welcome Email" progressText="Sending..." completedText="Sent" disabled={isSending || hasSent} />
                <TextInput className='new-campaign-subject' value={subject} onChange={(e: any) => setSubject(e.target.value)} disabled={isSending || hasSent} placeholder="Email Subject" />
                {hasSent && <a href={`/client-stocks/${combinedTicker}/Share Registry/Search?holderId=${holderId}`}>Back to Holder</a>}
            </div>
            <p className='welcome-email-info'>To: {name} &lt;{email}&gt; &mdash; sending will also add them as a subscriber.</p>

            <div className='tabs'>
                <ol className='tab-list'>
                    <Tab activeTab={activeTab} label='Editor' onClick={setActiveTab} />
                    <Tab activeTab={activeTab} label='Preview' onClick={setActiveTab} />
                </ol>
                <div className='tab-content'>
                {/* keep the editor mounted when Preview is active so TinyMCE doesn't re-initialise on every tab switch */}
                <div style={{ display: activeTab === 'Editor' ? undefined : 'none' }}>
                    {!editorReady && <p>Loading editor...</p>}
                    <Editor
                        onInit={() => setEditorReady(true)}
                        disabled={isSending || hasSent}
                        apiKey="fntw2t4q38u7usw5dk4ew34dbgz96zhpezwidg8edbp5jv1k"
                        value={editorContent}
                        onEditorChange={setEditorContent}
                        init={{
                            onboarding: false,
                            height: 800,
                            menubar: false,
                            visual: false,
                            plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'help', 'wordcount'
                            ],
                            toolbar: 'undo redo | blocks | bold italic underline code | ' +
                                'forecolor backcolor | alignleft aligncenter alignright alignjustify | ' +
                                'bullist numlist outdent indent | removeformat | ' +
                                'table tabledelete | tableprops tablerowprops tablecellprops | ' +
                                'tableinsertrowbefore tableinsertrowafter tabledeleterow | ' +
                                'tableinsertcolbefore tableinsertcolafter tabledeletecol | ' +
                                'link image media |  fullscreen',
                            toolbar_mode: 'sliding',
                            content_style: templateCss || 'body { font-family:Arial,sans-serif; font-size:14px }',
                            valid_elements: '*[*]',
                            extended_valid_elements: '*[*]',
                            valid_children: '+body[style],+div[style]',
                            verify_html: false,
                            cleanup: false,
                            convert_urls: false,
                            remove_trailing_brs: false,
                            entity_encoding: 'raw',
                        }}
                    />
                </div>

                {activeTab === 'Preview' && <iframe
                    srcDoc={fullHtml()}
                    style={{
                        width: '100%',
                        height: '800px',
                        border: '1px solid #ccc'
                    }}
                    title="HTML Preview"
                    sandbox="allow-same-origin"
                />}
                </div>
            </div>
        </>}
    </Style>;
};

export default SendWelcomeEmailPage;
